// SEC2-S3 (docs/build-briefs/SEC2-device-setup-code-binding.md section 4.7, DEC-321; design/mockups/
// sec2-s3-1 to sec2-s3-5): the setup screen. A new device is tied to a location by typing, on an in-page keypad, the
// ten character setup code a manager made in Settings > Devices. The device keyboard is never used for the code.
//
// What it does, in order: draws ten boxes and the keypad; on Connect, generates the device's own
// secret (ui/src/deviceCredential.ts), sends the code and the secret to POST /device-setup/redeem, and on
// success stores the credential in the terminal config (via deps.onConnected, which main.ts saves) and shows
// which business and location the device is now tied to. A retry after a lost answer reuses the same secret for the
// same code, which is what makes it safe: the server hands back the same device.
//
// The server answers every refusal identically, so this screen says one thing for every refusal (the model,
// ui/src/terminalSetupModel.ts). The mockup's "expired" variant is deliberately not built: it would need the
// server to say why. A throttled source is told how long to wait and the keys stay off until then.
//
// UX89: once the code is accepted the device is asked for a name (the Name this device step), then shown as set up.
// Save with the name unchanged makes no call. The name is sent once, with the device's own credential.
//
// Text is set with textContent only. The code and the secret are never logged and never put in a URL.

import { nameDeviceAtSetup, redeemSetupCode, type RedeemBody } from "./api.ts";
import { formatCountdown } from "./tabletsModel.ts";
import { generateDeviceSecret } from "./deviceCredential.ts";
import type { TerminalConfig } from "./session.ts";
import { terminalConfigFromRedemption } from "./session.ts";
import {
  KEYPAD_ROWS,
  SETUP_CODE_LENGTH,
  appendKey,
  codeBoxes,
  deleteLast,
  isComplete,
  normalizePastedCode,
} from "./setupCodeEntry.ts";
import {
  DEVICE_NAME_SUGGESTIONS,
  checkDeviceName,
  describeNameFailure,
  describeRedeemFailure,
  type RedeemFailureView,
} from "./terminalSetupModel.ts";

export interface TerminalSetupDeps {
  /** The server address to start from (the page's own origin when there is one). */
  defaultApiBaseUrl: () => string;
  /** The config now in force, if any (a device set up before this slice, being set up again). */
  currentConfig: () => TerminalConfig | undefined;
  /** A redemption succeeded: save the config and keep it as the terminal's own. */
  onConnected: (config: TerminalConfig) => void;
  /** "Go to sign in" on the success screen. */
  onContinue: () => void;
}

export interface TerminalSetupScreen {
  /** Shows the entry view with an empty code. `notice` is a message above the boxes (a disconnected device). */
  show: (options?: { notice?: string }) => void;
}

const SVG_INFO =
  '<svg class="setup-ico" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M10 9v5M10 5.8v1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

function find<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) {
    throw new Error(`expected element for selector "${selector}"`);
  }
  return element;
}

function hostOf(apiBaseUrl: string): string {
  try {
    return new URL(apiBaseUrl).host;
  } catch {
    return apiBaseUrl;
  }
}

export function mountTerminalSetup(
  root: HTMLElement,
  deps: TerminalSetupDeps,
): TerminalSetupScreen {
  const entryView = find<HTMLElement>(root, "#setup-entry-view");
  const successView = find<HTMLElement>(root, "#setup-success-view");
  const codeEl = find<HTMLElement>(root, "#setup-code");
  const noticeEl = find<HTMLElement>(root, "#setup-notice");
  const messageEl = find<HTMLElement>(root, "#setup-message");
  const pasteButton = find<HTMLButtonElement>(root, "#setup-paste");
  const pasteMessageEl = find<HTMLElement>(root, "#setup-paste-message");
  const form = find<HTMLFormElement>(root, "#terminal-setup-form");
  const connectButton = find<HTMLButtonElement>(root, "#setup-connect");
  const keysEl = find<HTMLElement>(root, "#setup-keys");
  const padEl = find<HTMLElement>(root, "#setup-pad");
  const deleteButton = find<HTMLButtonElement>(root, "#setup-delete");
  const clearButton = find<HTMLButtonElement>(root, "#setup-clear");
  const serverHostEl = find<HTMLElement>(root, "#setup-server-host");
  const changeServerButton = find<HTMLButtonElement>(root, "#setup-change-server");
  const serverEditEl = find<HTMLElement>(root, "#setup-server-edit");
  const serverInput = find<HTMLInputElement>(root, "#login-api-base-url");
  const serverUseButton = find<HTMLButtonElement>(root, "#setup-server-use");
  const serverCancelButton = find<HTMLButtonElement>(root, "#setup-server-cancel");
  const nameView = find<HTMLElement>(root, "#setup-name-view");
  const nameForm = find<HTMLFormElement>(root, "#setup-name-form");
  const nameLeadEl = find<HTMLElement>(root, "#setup-name-lead");
  const nameInput = find<HTMLInputElement>(root, "#setup-name-input");
  const nameSuggestionsEl = find<HTMLElement>(root, "#setup-name-suggestions");
  const nameMessageEl = find<HTMLElement>(root, "#setup-name-message");
  const nameSaveButton = find<HTMLButtonElement>(root, "#setup-name-save");
  const nameContinueButton = find<HTMLButtonElement>(root, "#setup-name-continue");
  const successBusinessEl = find<HTMLElement>(root, "#setup-success-business");
  const successLocationEl = find<HTMLElement>(root, "#setup-success-location");
  const successNameEl = find<HTMLElement>(root, "#setup-success-name");
  const goButton = find<HTMLButtonElement>(root, "#setup-go-to-sign-in");

  let code = "";
  let apiBaseUrl = "";
  // UX89: the config the naming step is for, and whether a save is in flight.
  let namingConfig: TerminalConfig | undefined;
  let naming = false;
  let failure: RedeemFailureView | undefined;
  let submitting = false;
  // Counts down a throttled source's wait. Cleared when it ends or the screen is shown again.
  let throttleTimer: ReturnType<typeof setInterval> | undefined;
  let throttleEndsAt = 0;
  // The secret for the code in the boxes. Kept across a retry of the SAME code (a lost answer), dropped when
  // the code changes, so a new code always gets a fresh secret.
  let pendingSecret: { forCode: string; secret: string } | undefined;
  const keyButtons: HTMLButtonElement[] = [];

  const throttled = (): boolean => throttleTimer !== undefined;

  for (const row of KEYPAD_ROWS) {
    for (const key of row) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "setup-key";
      button.textContent = key;
      button.dataset["testid"] = `setup-key-${key}`;
      button.setAttribute("aria-label", key);
      button.addEventListener("click", () => handleKey(key));
      keysEl.append(button);
      keyButtons.push(button);
    }
  }

  function stopThrottle(): void {
    if (throttleTimer !== undefined) {
      clearInterval(throttleTimer);
      throttleTimer = undefined;
    }
  }

  function messageIcon(): HTMLElement {
    const holder = document.createElement("span");
    holder.className = "setup-msg-icon";
    holder.setAttribute("aria-hidden", "true");
    // Static markup only, never data.
    holder.innerHTML = SVG_INFO;
    return holder;
  }

  function drawMessage(): void {
    messageEl.replaceChildren();
    if (failure === undefined) {
      messageEl.hidden = true;
      return;
    }
    messageEl.hidden = false;
    messageEl.dataset["kind"] = failure.kind;
    messageEl.classList.toggle(
      "setup-msg-warn",
      failure.kind === "network" || failure.kind === "server",
    );
    const text = document.createElement("span");
    const title = document.createElement("b");
    title.textContent = failure.title;
    text.append(title);
    if (failure.kind === "throttled" && failure.waitSeconds !== undefined) {
      const remaining = Math.max(0, throttleEndsAt - Date.now());
      const wait = document.createElement("b");
      wait.className = "setup-wait";
      // The seconds tick down every second; a screen reader reads the sentence once, not each tick.
      wait.setAttribute("aria-live", "off");
      wait.dataset["testid"] = "setup-wait";
      wait.textContent = formatCountdown(remaining);
      text.append(
        document.createTextNode("Wait "),
        wait,
        document.createTextNode(
          ", then try again. Or ask a manager to set up this device for you.",
        ),
      );
    } else {
      text.append(document.createTextNode(failure.detail));
    }
    messageEl.append(messageIcon(), text);
  }

  function draw(): void {
    const off = throttled() || submitting;
    codeEl.replaceChildren();
    const boxes = codeBoxes(code);
    boxes.forEach((box, index) => {
      if (index === 5) {
        const dash = document.createElement("span");
        dash.className = "setup-dash";
        dash.setAttribute("aria-hidden", "true");
        codeEl.append(dash);
      }
      const cell = document.createElement("span");
      cell.className = "setup-cell";
      cell.dataset["testid"] = `setup-cell-${index}`;
      cell.textContent = box.char;
      if (box.active && !off) cell.classList.add("is-current");
      if (failure !== undefined && failure.kind === "refused") cell.classList.add("is-bad");
      if (throttled()) cell.classList.add("is-off");
      codeEl.append(cell);
    });
    codeEl.setAttribute(
      "aria-label",
      `Setup code, ${code.length} of ${SETUP_CODE_LENGTH} characters entered`,
    );
    codeEl.dataset["entered"] = code;
    padEl.classList.toggle("is-off", off);
    for (const button of keyButtons) button.disabled = off;
    // Delete and Clear all are drawn live even with nothing entered (the approved mockup does), and do nothing then.
    deleteButton.disabled = off;
    clearButton.disabled = off;
    pasteButton.disabled = off;
    connectButton.disabled = off || !isComplete(code);
    connectButton.textContent = submitting ? "Connecting" : "Connect this device";
    drawMessage();
  }

  function clearFailure(): void {
    if (failure !== undefined && failure.kind !== "throttled") failure = undefined;
    hidePasteMessage();
  }

  function hidePasteMessage(): void {
    pasteMessageEl.hidden = true;
    pasteMessageEl.replaceChildren();
  }

  // UX84 (DEC-336): paste. Both paths (the Paste code button and a native paste event) end here. A clipboard that
  // cannot be read or holds nothing usable says so in one plain line and changes nothing; a usable one replaces
  // the boxes. It never submits: the existing Connect button does, so a stray paste cannot spend a code. The
  // text is never logged, stored or put in a URL; only the normalized characters reach the boxes.
  function applyPasted(text: string | undefined): void {
    if (throttled() || submitting) return;
    const pasted = text === undefined ? "" : normalizePastedCode(text);
    if (pasted === "") {
      const note = document.createElement("span");
      note.textContent = "Could not read the clipboard. Type the code on the keypad.";
      pasteMessageEl.replaceChildren(messageIcon(), note);
      pasteMessageEl.hidden = false;
      return;
    }
    clearFailure();
    code = pasted;
    draw();
  }

  pasteButton.addEventListener("click", () => {
    if (throttled() || submitting) return;
    const clipboard = typeof navigator === "undefined" ? undefined : navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.readText !== "function") {
      applyPasted(undefined);
      return;
    }
    clipboard.readText().then(
      (text) => applyPasted(text),
      () => applyPasted(undefined),
    );
  });

  // Cmd/Ctrl+V and a long-press Paste anywhere on the setup screen (the entry view only). The screen has no
  // text field for the code, so with nothing focused the event lands on the body, not on this section: the
  // listener sits on the document and checks that the entry view is the one showing. The server address field,
  // when open, keeps its own paste.
  document.addEventListener("paste", (event) => {
    if (entryView.hidden || entryView.getClientRects().length === 0) return;
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return;
    event.preventDefault();
    applyPasted(event.clipboardData?.getData("text/plain"));
  });

  function handleKey(key: string): void {
    if (throttled() || submitting) return;
    clearFailure();
    code = appendKey(code, key);
    draw();
  }

  // DEC-550: the physical keyboard also enters the code. Like paste, the listener sits on the document and acts
  // only while the entry view is showing; it stands down while any input has focus (the server address field)
  // and for Ctrl/Meta/Alt chords, so Cmd+V paste is untouched.
  document.addEventListener("keydown", (event) => {
    if (entryView.hidden || entryView.getClientRects().length === 0) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    )
      return;
    if (throttled() || submitting) return;
    if (event.key === "Backspace") {
      event.preventDefault();
      clearFailure();
      code = deleteLast(code);
      draw();
    } else if (event.key === "Enter") {
      if (target instanceof HTMLButtonElement || !isComplete(code)) return;
      event.preventDefault();
      void connect();
    } else if (event.key.length === 1) {
      event.preventDefault();
      handleKey(event.key);
    }
  });

  deleteButton.addEventListener("click", () => {
    if (throttled() || submitting) return;
    clearFailure();
    code = deleteLast(code);
    draw();
  });
  clearButton.addEventListener("click", () => {
    if (throttled() || submitting) return;
    clearFailure();
    code = "";
    draw();
  });

  function startThrottle(waitSeconds: number): void {
    stopThrottle();
    throttleEndsAt = Date.now() + waitSeconds * 1000;
    throttleTimer = setInterval(() => {
      if (Date.now() >= throttleEndsAt) {
        stopThrottle();
        failure = undefined;
      }
      draw();
    }, 1000);
  }

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void connect();
  });

  async function connect(): Promise<void> {
    if (submitting || throttled() || !isComplete(code)) return;
    submitting = true;
    failure = undefined;
    draw();
    if (pendingSecret === undefined || pendingSecret.forCode !== code) {
      pendingSecret = { forCode: code, secret: generateDeviceSecret() };
    }
    const secret = pendingSecret.secret;
    const result = await redeemSetupCode(apiBaseUrl, { code, deviceSecret: secret });
    submitting = false;
    if (result.status === 200 || result.status === 201) {
      const redeemed = result.body as RedeemBody;
      const config = terminalConfigFromRedemption(
        apiBaseUrl,
        redeemed,
        secret,
        deps.currentConfig(),
      );
      deps.onConnected(config);
      pendingSecret = undefined;
      code = "";
      showNameStep(config);
      return;
    }
    // A refusal means this code will not work with this secret, so the next try starts clean.
    if (result.status === 400) pendingSecret = undefined;
    const view = describeRedeemFailure({
      status: result.status,
      body: result.body as { error?: string; message?: string } | undefined,
      networkError: result.networkError,
      retryAfter: result.retryAfter,
    });
    failure = view;
    if (view.kind === "throttled") {
      startThrottle(view.waitSeconds ?? 300);
    }
    draw();
  }

  for (const suggestion of DEVICE_NAME_SUGGESTIONS) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "setup-secondary";
    chip.textContent = suggestion;
    chip.dataset["testid"] = "setup-name-suggestion";
    chip.addEventListener("click", () => {
      if (naming) return;
      nameInput.value = suggestion;
      hideNameMessage();
      nameInput.focus();
    });
    nameSuggestionsEl.append(chip);
  }

  function hideNameMessage(): void {
    nameMessageEl.hidden = true;
    nameMessageEl.replaceChildren();
  }

  function drawNaming(): void {
    nameInput.disabled = naming;
    nameSaveButton.disabled = naming;
    nameSaveButton.textContent = naming ? "Saving" : "Save";
    for (const chip of nameSuggestionsEl.querySelectorAll<HTMLButtonElement>("button")) {
      chip.disabled = naming;
    }
  }

  function showNameStep(config: TerminalConfig): void {
    stopThrottle();
    namingConfig = config;
    naming = false;
    entryView.hidden = true;
    successView.hidden = true;
    nameView.hidden = false;
    // UX102: the location's name and nothing else; the instruction sentences are gone.
    nameLeadEl.textContent = config.locationName ?? "";
    nameLeadEl.hidden = nameLeadEl.textContent === "";
    nameInput.value = config.deviceName ?? "";
    nameContinueButton.hidden = true;
    nameSaveButton.hidden = false;
    hideNameMessage();
    drawNaming();
    nameInput.focus();
    nameInput.select();
  }

  function showNameMessage(title: string, detail: string): void {
    const text = document.createElement("span");
    const heading = document.createElement("b");
    heading.textContent = title;
    text.append(heading, document.createTextNode(` ${detail}`));
    nameMessageEl.replaceChildren(messageIcon(), text);
    nameMessageEl.hidden = false;
  }

  nameInput.addEventListener("input", () => hideNameMessage());

  nameForm.addEventListener("submit", (event) => {
    event.preventDefault();
    void saveName();
  });

  nameContinueButton.addEventListener("click", () => {
    if (namingConfig !== undefined) showSuccess(namingConfig);
  });

  async function saveName(): Promise<void> {
    const config = namingConfig;
    if (config === undefined || naming) return;
    const check = checkDeviceName(nameInput.value, config.deviceName ?? "");
    if (check.kind === "invalid") {
      showNameMessage("Check the name.", check.message);
      nameInput.focus();
      return;
    }
    // Unchanged: no call, the device keeps the name it was given.
    if (check.kind === "unchanged") {
      showSuccess(config);
      return;
    }
    naming = true;
    hideNameMessage();
    drawNaming();
    const result = await nameDeviceAtSetup(
      config.apiBaseUrl,
      config.tenantId,
      config.deviceToken,
      check.name,
    );
    naming = false;
    drawNaming();
    if (result.status === 200 && "device" in result.body && result.body.device !== undefined) {
      const named: TerminalConfig = { ...config, deviceName: result.body.device.displayName };
      deps.onConnected(named);
      showSuccess(named);
      return;
    }
    const view = describeNameFailure({
      status: result.status,
      body: result.body as { error?: string; message?: string } | undefined,
      networkError: result.networkError,
    });
    showNameMessage(view.title, view.detail);
    // A closed window cannot be retried: offer the way on, keeping the current name.
    if (view.kind === "closed") {
      nameSaveButton.hidden = true;
      nameInput.value = config.deviceName ?? "";
      nameInput.disabled = true;
      for (const chip of nameSuggestionsEl.querySelectorAll<HTMLButtonElement>("button")) {
        chip.disabled = true;
      }
      nameContinueButton.hidden = false;
      nameContinueButton.focus();
    }
  }

  function showSuccess(config: TerminalConfig): void {
    stopThrottle();
    entryView.hidden = true;
    nameView.hidden = true;
    successView.hidden = false;
    successBusinessEl.textContent = config.businessName ?? "";
    successLocationEl.textContent = config.locationName ?? "";
    successNameEl.textContent = config.deviceName ?? "";
    goButton.focus();
  }

  goButton.addEventListener("click", () => {
    successView.hidden = true;
    entryView.hidden = false;
    namingConfig = undefined;
    deps.onContinue();
  });

  function setServer(next: string): void {
    apiBaseUrl = next.trim().replace(/\/$/, "");
    serverHostEl.textContent = hostOf(apiBaseUrl);
  }

  changeServerButton.addEventListener("click", () => {
    serverInput.value = apiBaseUrl;
    serverEditEl.hidden = false;
    changeServerButton.hidden = true;
  });
  serverCancelButton.addEventListener("click", () => {
    serverEditEl.hidden = true;
    changeServerButton.hidden = false;
  });
  serverUseButton.addEventListener("click", () => {
    if (serverInput.value.trim() !== "") {
      setServer(serverInput.value);
      pendingSecret = undefined;
    }
    serverEditEl.hidden = true;
    changeServerButton.hidden = false;
  });

  function show(options: { notice?: string } = {}): void {
    stopThrottle();
    code = "";
    failure = undefined;
    submitting = false;
    pendingSecret = undefined;
    hidePasteMessage();
    setServer(deps.currentConfig()?.apiBaseUrl ?? deps.defaultApiBaseUrl());
    serverEditEl.hidden = true;
    changeServerButton.hidden = false;
    entryView.hidden = false;
    successView.hidden = true;
    nameView.hidden = true;
    nameSaveButton.hidden = false;
    namingConfig = undefined;
    if (options.notice === undefined) {
      noticeEl.hidden = true;
      noticeEl.replaceChildren();
    } else {
      noticeEl.hidden = false;
      noticeEl.classList.add("setup-msg-warn");
      const text = document.createElement("span");
      text.textContent = options.notice;
      noticeEl.replaceChildren(messageIcon(), text);
    }
    draw();
  }

  return { show };
}
